import { useState, useEffect, useCallback } from 'react';
import useApi from '../../api';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Search, Hash, Info } from 'lucide-react';
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useUser } from '../../user/UserContext';

interface Channel {
    id: string;
    name: string;
}

// Scopes the Slack app needs for two-way live chat takeover; installs that
// predate them must reconnect Slack to re-consent.
const LIVE_CHAT_SCOPES = ['channels:history', 'groups:history', 'chat:write.customize'];

interface NotificationConfig {
    channelId: string;
    liveChat?: boolean;
    notifications: {
        newVisitor: boolean;
        newLead: boolean;
        sessionActive: boolean;
        visitorIdentified: boolean;
        newQuestion: boolean;
        sessionEscalation?: boolean;
        buyerIntent?: boolean;
        assessmentCompleted?: boolean;
        reportGenerated?: boolean;
        visitorClassification?: boolean;
        highFitAccount?: boolean;
        emailConversation?: boolean;
        sessionFlagged?: boolean;
        warmVisitor?: boolean;
    };
}

interface SlackNotificationsProps {
    integration: any;
    value: any;
    onChange: (value: any) => void;
    escalationEnabled: boolean;
    enableTracks: boolean;
    actions?: Record<string, any>;
}

export default function SlackNotifications({ integration, value, onChange, escalationEnabled, enableTracks, actions }: SlackNotificationsProps) {
    const { get } = useApi();
    const { organization } = useUser();
    const accountResearchEnabled = Boolean(organization?.settings?.account_research?.enabled);
    const getDefaultNotifications = useCallback((): NotificationConfig['notifications'] => {
        const notifications: NotificationConfig['notifications'] = {
            newVisitor: true,
            newLead: true,
            sessionActive: true,
            visitorIdentified: true,
            newQuestion: true
        };

        // Escalations are skill-driven now (not the deprecated actions
        // escalation config), so the notification is always applicable.
        notifications.sessionEscalation = false;

        // Inbound email conversations (email channel) — off by default:
        // async channel, opt-in visibility.
        notifications.emailConversation = false;

        // Session moderation verdicts — off by default, opt-in per channel.
        notifications.sessionFlagged = false;
        // Warm visitors (intent-scored browsing) — off by default: fires for
        // anonymous traffic too, so a busy site can be noisy.
        notifications.warmVisitor = false;

        // Add buyer intent notifications if buyer intent tool is configured
        if (actions?.buyer_intent) {
            notifications.buyerIntent = false;
        }

        // Add assessment notifications if assessments are configured
        if (actions?.assessments && actions.assessments.length > 0) {
            notifications.assessmentCompleted = false;
        }

        // Add report notifications if reports are configured
        if (actions?.reports && actions.reports.length > 0) {
            notifications.reportGenerated = false;
        }

        // Add visitor classification notifications if tracks are enabled
        if (enableTracks) {
            notifications.visitorClassification = false;
        }

        // Account research is an org-level feature flag, not an agent action
        if (accountResearchEnabled) {
            notifications.highFitAccount = false;
        }

        return notifications;
    }, [
        enableTracks,
        accountResearchEnabled,
        actions?.buyer_intent,
        actions?.assessments,
        actions?.reports
    ]);

    const ensureApplicableNotifications = useCallback((configsToNormalize: NotificationConfig[]) => {
        const defaults = getDefaultNotifications();
        let hasChanges = false;

        const normalizedConfigs = configsToNormalize.map((config) => {
            let configChanged = false;
            const nextNotifications = { ...config.notifications };

            (Object.keys(defaults) as Array<keyof NotificationConfig['notifications']>).forEach((key) => {
                if (nextNotifications[key] === undefined) {
                    nextNotifications[key] = defaults[key] ?? false;
                    configChanged = true;
                }
            });

            if (!configChanged) {
                return config;
            }

            hasChanges = true;
            return {
                ...config,
                notifications: nextNotifications
            };
        });

        return hasChanges ? normalizedConfigs : configsToNormalize;
    }, [getDefaultNotifications]);

    const [channels, setChannels] = useState<Channel[]>([]);
    const [loading, setLoading] = useState(true);
    const [configs, setConfigs] = useState<NotificationConfig[]>(() =>
        ensureApplicableNotifications(value?.configs || [])
    );
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        fetchChannels();
    }, []);

    useEffect(() => {
        setConfigs(ensureApplicableNotifications(value?.configs || []));
    }, [value?.configs, ensureApplicableNotifications]);

    const fetchChannels = async () => {
        try {
            const response = await get(`/slack/channels/${integration.id}`);
            setChannels(response.data);
        } catch (error) {
            console.error('Error fetching channels:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleChannelToggle = (channelId: string) => {
        setConfigs((prevConfigs) => {
            const existingConfig = prevConfigs.find(c => c.channelId === channelId);

            if (existingConfig) {
                return prevConfigs.filter(c => c.channelId !== channelId);
            }

            return [
                ...prevConfigs,
                {
                    channelId,
                    notifications: getDefaultNotifications()
                }
            ];
        });
    };

    const grantedScopes: string[] = integration?.details?.scopes || [];
    const hasLiveChatScopes = LIVE_CHAT_SCOPES.every(scope => grantedScopes.includes(scope));

    const handleLiveChatToggle = (channelId: string) => {
        setConfigs((prevConfigs) =>
            prevConfigs.map(config =>
                config.channelId === channelId
                    ? { ...config, liveChat: !config.liveChat }
                    : config
            )
        );
    };

    const handleNotificationToggle = (channelId: string, notificationType: keyof NotificationConfig['notifications']) => {
        setConfigs((prevConfigs) => {
            return prevConfigs.map(config => {
                if (config.channelId === channelId) {
                    return {
                        ...config,
                        notifications: {
                            ...config.notifications,
                            [notificationType]: !config.notifications[notificationType]
                        }
                    };
                }
                return config;
            });
        });
    };

    useEffect(() => {
        onChange({ configs });
    }, [configs, onChange]);

    const filteredChannels = channels.filter(channel => 
        channel.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (loading) {
        return (
            <div className="animate-pulse space-y-4">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="h-24 bg-muted rounded-lg"></div>
                ))}
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="relative">
                <Input
                    type="text"
                    placeholder="Search channels..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            </div>

            <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                    <strong>Reminder:</strong> Make sure to add the Aimdoc bot to your selected Slack channels after configuring them here in order to receive notifications.
                </AlertDescription>
            </Alert>

            <ScrollArea className="h-[400px]">
                <div className="space-y-4 pr-4">
                    {filteredChannels.map(channel => {
                        const config = configs.find(c => c.channelId === channel.id);
                        return (
                            <div key={channel.id} className="p-4 rounded-lg border bg-card">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                        <Hash className="h-4 w-4 text-muted-foreground" />
                                        <span className="text-lg font-medium">
                                            {channel.name}
                                        </span>
                                    </div>
                                    <Button
                                        onClick={() => handleChannelToggle(channel.id)}
                                        variant={config ? "destructive" : "default"}
                                        size="sm"
                                    >
                                        {config ? 'Remove' : 'Add'}
                                    </Button>
                                </div>
                                {config && (
                                    <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t">
                                        {Object.entries(config.notifications).map(([key, value]) => (
                                            <label key={key} className="flex items-center space-x-3">
                                                <Checkbox
                                                    checked={value}
                                                    onCheckedChange={() => 
                                                        handleNotificationToggle(channel.id, key as keyof NotificationConfig['notifications'])
                                                    }
                                                />
                                                <span className="text-sm">
                                                    {key === 'newVisitor' ? 'New Visitor' :
                                                     key === 'newLead' ? 'New Contact' :
                                                     key === 'sessionActive' ? 'Session Active' :
                                                     key === 'visitorIdentified' ? 'Visitor Identified' :
                                                     key === 'sessionEscalation' ? 'Session Escalation' :
                                                     key === 'buyerIntent' ? 'Buyer Intent' :
                                                     key === 'assessmentCompleted' ? 'Assessment Completed' :
                                                     key === 'reportGenerated' ? 'Report Generated' :
                                                     key === 'visitorClassification' ? 'Visitor Classification' :
                                                     key === 'highFitAccount' ? 'High-Fit Account' :
                                                     key === 'emailConversation' ? 'Email Conversation' :
                                                     key === 'sessionFlagged' ? 'Session Flagged' :
                                                     key === 'warmVisitor' ? 'Warm Visitor' :
                                                     'New Question'}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                                {config && config.notifications.sessionEscalation && (
                                    <div className="mt-4 pt-4 border-t">
                                        <label className={cn(
                                            "flex items-start space-x-3",
                                            !hasLiveChatScopes && "opacity-60"
                                        )}>
                                            <Checkbox
                                                checked={Boolean(config.liveChat)}
                                                disabled={!hasLiveChatScopes}
                                                onCheckedChange={() => handleLiveChatToggle(channel.id)}
                                            />
                                            <span className="text-sm">
                                                <span className="font-medium">Live Chat Takeover</span>
                                                <span className="block text-muted-foreground">
                                                    {hasLiveChatScopes
                                                        ? 'Escalations post as an interactive card. Reps reply to the visitor directly from the Slack thread and can take over or hand back to the AI.'
                                                        : 'Reconnect Slack in Integrations to grant the additional permissions live chat requires.'}
                                                </span>
                                            </span>
                                        </label>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    
                    {filteredChannels.length === 0 && searchQuery && (
                        <div className="text-center p-8 border border-dashed rounded-lg">
                            <p className="text-muted-foreground">
                                No channels found matching "{searchQuery}"
                            </p>
                        </div>
                    )}
                    
                    {channels.length === 0 && !searchQuery && (
                        <div className="text-center p-8 border border-dashed rounded-lg">
                            <p className="text-muted-foreground">
                                No channels found. Make sure you have channels in your Slack workspace.
                            </p>
                        </div>
                    )}
                </div>
            </ScrollArea>
        </div>
    );
} 